import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useBrand, useProductName } from "@/context/BrandContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone } from "@phosphor-icons/react";
import CaptchaWidget, { resetCaptcha } from "@/components/CaptchaWidget";

export default function ForgotPassword() {
  const { brand } = useBrand();
  const productName = useProductName();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);

  const onCaptcha = useCallback((token) => setCaptchaToken(token), []);
  const onCaptchaEnabled = useCallback((enabled) => {
    setCaptchaRequired(!!enabled);
    if (!enabled) setCaptchaToken(null);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (captchaRequired && !captchaToken) {
      setError("Please complete the captcha.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim(),
        captcha_token: captchaToken || undefined,
        host: typeof window !== "undefined" ? window.location.hostname : undefined,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err) || err.message);
      resetCaptcha();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const logo = brand.logo_url || brand.login_logo_url;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          {logo ? (
            <img src={logo} alt="" className="h-10 w-10 object-contain" />
          ) : (
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Phone className="h-5 w-5" weight="fill" />
            </div>
          )}
          <p className="font-display font-bold tracking-tight">{productName || "Account"}</p>
        </div>

        {done ? (
          <div data-testid="forgot-password-done">
            <h1 className="text-2xl font-display font-semibold tracking-tight">Check your email</h1>
            <p className="text-sm text-muted-foreground mt-2">
              If an account exists for <span className="font-medium text-foreground">{email}</span>,
              we sent a password reset link.
            </p>
            <Link to="/login" className="mt-8 inline-flex h-11 w-full items-center justify-center rounded-lg bg-primary text-sm font-medium text-primary-foreground">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-display font-semibold tracking-tight">Forgot password</h1>
            <p className="text-sm text-muted-foreground mt-1.5">
              Enter your email and we&apos;ll send a reset link for your {productName || "account"}.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email"
                  className="h-11"
                  placeholder="you@company.com"
                />
              </div>
              <CaptchaWidget onChange={onCaptcha} onEnabled={onCaptchaEnabled} />
              {error && <p className="text-sm text-destructive" data-testid="forgot-password-error">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="forgot-password-submit" className="w-full h-11 rounded-lg">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground mt-6 text-center">
              <Link to="/login" className="text-primary font-medium hover:underline">Back to sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
